import { useEffect, useState } from "react";
import { X, ArrowRight, Sparkles, BookOpen, Search, MessageSquare } from "lucide-react";
import { Button } from "./ui/button";

const TOUR_KEY = "brimi_tour_v1_seen";

const STEPS = [
  {
    icon: Sparkles,
    title: "Welcome to ScienceLab",
    body: "Your AI-powered ICSE Physics & Chemistry tutor. Notes, quizzes, mock tests and a chat tutor — all in one place.",
  },
  {
    icon: BookOpen,
    title: "Pick a Chapter",
    body: "Choose your class (IX or X) and subject, then tap any chapter to open AI-generated notes, quizzes, and sample board papers.",
  },
  {
    icon: Search,
    title: "Search & Chat",
    body: "Use the search bar to jump to any topic instantly. Inside a chapter, tap the Chat tab to ask doubts or snap a photo of a question for an instant step-by-step solution.",
  },
  {
    icon: MessageSquare,
    title: "Help us improve",
    body: "Tap the 'Rate Us' pill at the bottom-right any time to share feedback. Your input shapes what we build next.",
  },
];

export default function OnboardingTour() {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    try {
      const seen = localStorage.getItem(TOUR_KEY);
      if (!seen) {
        // small delay so it doesn't collide with page mount
        const t = setTimeout(() => setOpen(true), 700);
        return () => clearTimeout(t);
      }
    } catch (_) { /* localStorage disabled — silently skip tour */ }
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(TOUR_KEY, "1"); } catch (_) { /* noop */ }
    setOpen(false);
  };

  if (!open) return null;
  const cur = STEPS[step];
  const Icon = cur.icon;
  const isLast = step === STEPS.length - 1;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      data-testid="onboarding-tour"
      role="dialog"
      aria-modal="true"
    >
      <div className="brut-card rounded-none bg-white text-[#0F1120] max-w-md w-full p-6 sm:p-8 relative">
        <button
          onClick={dismiss}
          data-testid="onboarding-close-btn"
          aria-label="Skip tour"
          className="absolute top-3 right-3 w-9 h-9 brut-border bg-white flex items-center justify-center hover:bg-neutral-100">
          <X size={18} strokeWidth={2.5} />
        </button>

        <div className="w-14 h-14 brut-border bg-[#FFD93D] flex items-center justify-center mb-4">
          <Icon size={26} strokeWidth={2.5} />
        </div>

        <div className="text-xs font-bold tracking-[0.25em] uppercase text-neutral-500 mb-1">
          Step {step + 1} of {STEPS.length}
        </div>
        <h2 className="text-2xl font-black tracking-tight mb-3" data-testid="onboarding-title">
          {cur.title}
        </h2>
        <p className="text-neutral-700 leading-relaxed mb-6">{cur.body}</p>

        <div className="flex items-center justify-between gap-3">
          <div className="flex gap-1.5">
            {STEPS.map((_, i) => (
              <div
                key={i}
                className={`h-2 rounded-full transition-all ${i === step ? "w-8 bg-[#0F1120]" : "w-2 bg-neutral-300"}`}
              />
            ))}
          </div>
          <div className="flex gap-2">
            {!isLast && (
              <Button
                onClick={dismiss}
                data-testid="onboarding-skip-btn"
                className="brut-btn rounded-none bg-white text-[#0F1120] hover:bg-white">
                Skip
              </Button>
            )}
            <Button
              onClick={() => (isLast ? dismiss() : setStep((s) => s + 1))}
              data-testid="onboarding-next-btn"
              className="brut-btn rounded-none bg-[#1A1A1A] text-white hover:bg-[#1A1A1A]">
              {isLast ? "Get Started" : "Next"}
              <ArrowRight size={16} className="ml-1" strokeWidth={2.5} />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
